import React, { useState, useRef, useEffect } from "react";
import moment from "moment";

const DatePicker = ({ selectedDate, onDateChange }) => {
    const [isOpen, setIsOpen] = useState(false);

    // 計算最近 4 個月
    const recentMonths = Array.from({ length: 4 }, (_, i) =>
        moment().subtract(i, "months")
    ).reverse(); // 確保從最舊的排到最新的
    
    const allowedYears = [...new Set(recentMonths.map((m) => m.year()))];
    const monthMap = {}; // 建立年份對應的月份列表

    recentMonths.forEach((m) => {
        const year = m.year();
        if (!monthMap[year]) monthMap[year] = [];
        monthMap[year].push(m.month() + 1);
    });

    const [selectedYear, setSelectedYear] = useState(moment(selectedDate).year());
    const [selectedMonth, setSelectedMonth] = useState(moment(selectedDate).month() + 1);
    const [displayedDate, setDisplayedDate] = useState(`${selectedMonth < 10 ? `0${selectedMonth}` : selectedMonth} / ${selectedYear}`);
    
    const yearRef = useRef(null);
    const monthRef = useRef(null);
    const itemHeight = 44; // 每个 item 高度

    // 當年份變更時，確保 `selectedMonth` 仍在該年份允許的月份內
    // 如果 `selectedMonth` 不在 `monthMap[selectedYear]` 內，則重設為該年份的第一個合法月份
    useEffect(() => {
        if (!monthMap[selectedYear].includes(selectedMonth)) {
            setSelectedMonth(monthMap[selectedYear][0]);
        }
    }, [selectedYear]);

    const useDragScroll = (ref, setSelected, items) => {
        const [isDragging, setIsDragging] = useState(false);
        const [startY, setStartY] = useState(0);
        const [scrollTop, setScrollTop] = useState(0);

        const handleMouseDown = (e) => {
            setIsDragging(true);
            setStartY(e.clientY);
            setScrollTop(ref.current.scrollTop);
        };

        const handleMouseMove = (e) => {
            if (!isDragging) return;
            const deltaY = e.clientY - startY;
            ref.current.scrollTop = scrollTop - deltaY;

            const centerIndex = Math.round(ref.current.scrollTop / itemHeight);
            const newValue = items[Math.min(Math.max(centerIndex, 0), items.length - 1)];
            setSelected(newValue);
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        return { handleMouseDown, handleMouseMove, handleMouseUp };
    };

    const yearEvents = useDragScroll(yearRef, setSelectedYear, allowedYears);
    const monthEvents = useDragScroll(monthRef, setSelectedMonth, monthMap[selectedYear]);

    // **確保展開時滾動對齊選中的年月**
    useEffect(() => {
        if (isOpen) {
            const selectedYearIndex = allowedYears.indexOf(selectedYear);
            const selectedMonthIndex = monthMap[selectedYear].indexOf(selectedMonth);

            if (yearRef.current) {
                yearRef.current.scrollTop = selectedYearIndex * itemHeight;
            }
            if (monthRef.current) {
                monthRef.current.scrollTop = selectedMonthIndex * itemHeight;
            }
        }
    }, [isOpen, selectedYear, selectedMonth]);

    const handleConfirm = () => {
        setIsOpen(false);
        setDisplayedDate(`${selectedMonth < 10 ? `0${selectedMonth}` : selectedMonth} / ${selectedYear}`);
    };

    const handleSearch = () => {
        if (onDateChange) {
            const startOfMonth = moment(`${selectedYear}-${selectedMonth}`, "YYYY-M").startOf("month");
            const endOfMonth = moment(`${selectedYear}-${selectedMonth}`, "YYYY-M").endOf("month");
    
            onDateChange({ from: startOfMonth, to: endOfMonth });
        }
    };

    return (
        <div className="date-picker-container">
            <div
                className="date-input-container"
                onClick={() => {
                    setIsOpen(true);
                }}
            >
                <div className="date-input">
                    {displayedDate}
                </div>
                <div className="icon">
                    <img src="/th/img/icon/calendar.svg" alt="calendar" />
                </div>
            </div>
            <button className="search-btn" onClick={handleSearch}>
                ค้นหา
            </button>

            {isOpen && (
                <div className="date-picker-dropdown">
                    <div className="dropdown-wrapper">
                        <div className="gradient-overlay top"></div>
                        <div className="gradient-overlay bottom"></div>
                        <div className="highlight-bar"></div>
                        <div
                            className="dropdown"
                            ref={monthRef}
                            onMouseDown={monthEvents.handleMouseDown}
                            onMouseMove={monthEvents.handleMouseMove}
                            onMouseLeave={monthEvents.handleMouseUp}
                            onMouseUp={monthEvents.handleMouseUp}
                        >
                            <ul className="dropdown-content">
                                {monthMap[selectedYear].map((month) => (
                                    <li
                                        key={month}
                                        className={
                                            selectedMonth === month
                                                ? "selected"
                                                : ""
                                        }
                                    >
                                        {month < 10 ? `0${month}` : month}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div
                            className="dropdown"
                            ref={yearRef}
                            onMouseDown={yearEvents.handleMouseDown}
                            onMouseMove={yearEvents.handleMouseMove}
                            onMouseLeave={yearEvents.handleMouseUp}
                            onMouseUp={yearEvents.handleMouseUp}
                        >
                            <ul className="dropdown-content">
                                {allowedYears.map((year) => (
                                    <li
                                        key={year}
                                        className={
                                        selectedYear === year ? "selected" : ""
                                        }
                                    >
                                        {year}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div className="confirm-btn-wrapper">
                        <button className="confirm-btn" onClick={handleConfirm}>
                            ยืนยัน
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DatePicker;
